import { Component, Input, OnInit, output } from '@angular/core'
import { MapCpoFilterViewOption } from 'src/app/types/map-cpo-filter-view-option.interface';
import { NO_OPERATOR_SELECTED } from 'src/app/services/cpo-filter-options.service';

export interface OperatorFilterSelection {
  cpos: string[];
  operatorIds: string[];
}

@Component({
    selector: 'yf-filter-cpo-selector',
    templateUrl: './filter-cpo-selector.component.html',
    styleUrls: ['./filter-cpo-selector.component.scss'],
    standalone: false
})
export class FilterCpoSelectorComponent implements OnInit {
  @Input() cpos: MapCpoFilterViewOption[] = [];
  @Input() selectedCpos: string[] = [];
  @Input() selectedOperatorIds: string[] = [];
  filterOutput = output<OperatorFilterSelection>();

  readonly allOption: MapCpoFilterViewOption = {
    key: 'ALL',
    label: 'APP.FILTER.ALL_OPERATORS',
    icon: 'apps-outline',
    all: true,
  };

  constructor() { }

  ngOnInit() {
  }

  get displayOptions(): MapCpoFilterViewOption[] {
    return [this.allOption, ...(this.cpos || [])];
  }

  selectOperator(option: MapCpoFilterViewOption) {
    if (option.all) {
      if (this.isAllSelected()) {
        this.selectedCpos = [];
        this.selectedOperatorIds = [NO_OPERATOR_SELECTED];
      } else {
        this.selectedCpos = [];
        this.selectedOperatorIds = [];
      }
      this.emitFilters();
      return;
    }

    const allSelectable = this.getAllSelectableSelection();
    let selectedCpos = this.isAllSelected()
      ? [...allSelectable.cpos]
      : [...(this.selectedCpos || [])];
    let selectedOperatorIds = this.isAllSelected()
      ? [...allSelectable.operatorIds]
      : [...(this.selectedOperatorIds || [])];

    if (option.cpo) {
      selectedCpos = this.toggleValue(selectedCpos, option.cpo);
    }

    for (const operatorId of option.operatorIds || []) {
      selectedOperatorIds = this.toggleValue(
        selectedOperatorIds,
        operatorId,
        (value) => this.normalizeOperatorId(value),
      );
    }

    const normalizedSelection = this.normalizeSelection({
      cpos: selectedCpos,
      operatorIds: selectedOperatorIds,
    });

    this.selectedCpos = normalizedSelection.cpos;
    this.selectedOperatorIds = normalizedSelection.operatorIds;
    this.emitFilters();
  }

  isSelected(option: MapCpoFilterViewOption): boolean {
    if (this.isAllSelected()) {
      return true;
    }

    if (option.all) {
      return false;
    }

    const selectedCpos = this.selectedCpos || [];
    const selectedOperatorIds = (this.selectedOperatorIds || []).map((operatorId) => this.normalizeOperatorId(operatorId));
    const cpoSelected = option.cpo ? selectedCpos.includes(option.cpo) : false;
    const operatorSelected = (option.operatorIds || []).some((operatorId) =>
      selectedOperatorIds.includes(this.normalizeOperatorId(operatorId)),
    );

    return cpoSelected || operatorSelected;
  }

  private isAllSelected(): boolean {
    const selectedCpos = this.selectedCpos || [];
    const selectedOperatorIds = this.selectedOperatorIds || [];
    if (selectedCpos.length === 0 && selectedOperatorIds.length === 0) {
      return true;
    }

    const allSelectable = this.getAllSelectableSelection();
    return this.sameValues(selectedCpos, allSelectable.cpos)
      && this.sameValues(
        selectedOperatorIds,
        allSelectable.operatorIds,
        (value) => this.normalizeOperatorId(value),
      );
  }

  private toggleValue(
    values: string[],
    value: string,
    normalizer: (value: string) => string = (entry) => entry,
  ): string[] {
    const normalizedValue = normalizer(value);
    if (values.some((item) => normalizer(item) === normalizedValue)) {
      return values.filter((item) => normalizer(item) !== normalizedValue);
    } else {
      return [...values, value];
    }
  }

  private normalizeSelection(selection: OperatorFilterSelection): OperatorFilterSelection {
    const allSelectable = this.getAllSelectableSelection();
    const allowedCpos = new Set(allSelectable.cpos);
    const allowedOperatorIds = new Set(
      allSelectable.operatorIds.map((value) => this.normalizeOperatorId(value)),
    );

    const cpos = this.uniqueValues(selection.cpos || []).filter((value) =>
      allowedCpos.has(value),
    );
    const operatorIds = this.uniqueValues(
      selection.operatorIds || [],
      (value) => this.normalizeOperatorId(value),
    ).filter((value) =>
      allowedOperatorIds.has(this.normalizeOperatorId(value)),
    );

    if (
      this.sameValues(cpos, allSelectable.cpos)
      && this.sameValues(
        operatorIds,
        allSelectable.operatorIds,
        (value) => this.normalizeOperatorId(value),
      )
    ) {
      return { cpos: [], operatorIds: [] };
    }

    return { cpos, operatorIds };
  }

  private getAllSelectableSelection(): OperatorFilterSelection {
    return {
      cpos: this.uniqueValues(
        (this.cpos || []).reduce<string[]>((values, option) => {
          if (option.cpo) {
            values.push(option.cpo);
          }
          return values;
        }, []),
      ),
      operatorIds: this.uniqueValues(
        (this.cpos || []).reduce<string[]>((values, option) => {
          values.push(...(option.operatorIds || []));
          return values;
        }, []),
        (value) => this.normalizeOperatorId(value),
      ),
    };
  }

  private uniqueValues(
    values: string[],
    normalizer: (value: string) => string = (entry) => entry,
  ): string[] {
    const seen = new Set<string>();
    return values.filter((value) => {
      const normalizedValue = normalizer(value);
      if (seen.has(normalizedValue)) {
        return false;
      }
      seen.add(normalizedValue);
      return true;
    });
  }

  private sameValues(
    first: string[],
    second: string[],
    normalizer: (value: string) => string = (entry) => entry,
  ): boolean {
    if (first.length !== second.length) {
      return false;
    }

    const firstSet = new Set(first.map((value) => normalizer(value)));
    const secondSet = new Set(second.map((value) => normalizer(value)));
    if (firstSet.size !== secondSet.size) {
      return false;
    }

    return Array.from(firstSet).every((value) => secondSet.has(value));
  }

  private emitFilters() {
    this.filterOutput.emit({
      cpos: [...(this.selectedCpos || [])],
      operatorIds: [...(this.selectedOperatorIds || [])],
    });
  }

  private normalizeOperatorId(value?: string): string {
    return (value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  }
}
